import _extends from "@babel/runtime/helpers/extends";
import styled from 'styled-components';
import { observer } from 'mobx-react';
import { Menu } from 'core/components';
import { SortableContainer } from 'react-sortable-hoc';
import { MetricOrDimensionItem } from 'app/views/boards/MetricOrDimensionItem';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import Container from 'core/components/Container';
import { getAnimationStyle, ANIMATION_DURATION_MS } from './selectionAnimations';
const WrappedMenu = styled(Menu).withConfig({
  displayName: "SortableMenu__WrappedMenu",
  componentId: "sc-1jwiuo-0"
})(["&&{display:flex;flex-direction:column;gap:6px;}"]);
const SortableInnerContainer = ({
  items,
  getRollupFns,
  onRollupChange,
  measurement,
  onAnimatedRemove,
  type,
  measurements,
  enteringItems,
  exitingItems,
  customPercentiles
}) => /*#__PURE__*/React.createElement(WrappedMenu, {
  bg: "transparent",
  p: 0
}, items.map((item, index) => /*#__PURE__*/React.createElement(MetricOrDimensionItem, {
  index: index,
  reorderable: true,
  key: item.name,
  measurement: measurement,
  value: item.name,
  type: type,
  onRemove: () => {
    onAnimatedRemove(item.name);
  },
  rollupFns: getRollupFns ? getRollupFns(item.name) : undefined,
  onRollupChange: onRollupChange ? fns => onRollupChange(item.name, fns) : undefined,
  customPercentiles: customPercentiles,
  measurements: measurements,
  isEntering: enteringItems.has(item.name),
  isExiting: exitingItems.has(item.name)
})));

// Wrap with observer to ensure MobX reactivity propagates through SortableContainer
const InnerContainer = SortableContainer(observer(SortableInnerContainer));
export const SortableMenu = ({
  onSortEnd,
  getRollupFns,
  onRollupChange,
  customPercentiles,
  handleRemoveItem,
  items,
  ...props
}) => {
  const prevItemsRef = useRef(new Set(items.map(i => i.name)));
  const [enteringItems, setEnteringItems] = useState(new Set());
  const [exitingItems, setExitingItems] = useState(new Set());
  const removeTimersRef = useRef(new Map());

  // Clear all remove timers on unmount to avoid state updates after unmount
  useEffect(() => () => {
    removeTimersRef.current.forEach(id => clearTimeout(id));
    removeTimersRef.current.clear();
  }, []);

  // Detect newly added items
  useEffect(() => {
    const style = getAnimationStyle();
    if (style === 'none') {
      prevItemsRef.current = new Set(items.map(i => i.name));
      return undefined;
    }
    const currentNames = new Set(items.map(i => i.name));
    const newItems = new Set();
    for (const name of currentNames) {
      if (!prevItemsRef.current.has(name)) {
        newItems.add(name);
      }
    }
    prevItemsRef.current = currentNames;
    if (newItems.size > 0) {
      setEnteringItems(newItems);
      const id = setTimeout(() => setEnteringItems(new Set()), ANIMATION_DURATION_MS);
      return () => clearTimeout(id);
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  // Animated remove: play exit animation, then call real handler
  const onAnimatedRemove = useCallback(name => {
    const style = getAnimationStyle();
    if (style === 'none') {
      handleRemoveItem(name);
      return;
    }
    setExitingItems(prev => new Set(prev).add(name));
    const existing = removeTimersRef.current.get(name);
    if (existing !== undefined) {
      clearTimeout(existing);
    }
    removeTimersRef.current.set(name, setTimeout(() => {
      removeTimersRef.current.delete(name);
      setExitingItems(prev => {
        const next = new Set(prev);
        next.delete(name);
        return next;
      });
      handleRemoveItem(name);
    }, ANIMATION_DURATION_MS));
  }, [handleRemoveItem]);
  return /*#__PURE__*/React.createElement(Container, _extends({
    width: "100%",
    useDragHandle: true
  }, props), /*#__PURE__*/React.createElement(InnerContainer, _extends({
    useDragHandle: true,
    onSortEnd: onSortEnd,
    getRollupFns: getRollupFns,
    onRollupChange: onRollupChange,
    customPercentiles: customPercentiles,
    onAnimatedRemove: onAnimatedRemove,
    items: items,
    enteringItems: enteringItems,
    exitingItems: exitingItems
  }, props)));
};